
import { useEffect } from "react";
import "./ViewActiveRule.css";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import resetIcon from "../../../../common/assets/images/reseticon.png";
import {
  viewCodeAssistRules,
  clearRuleDraft,
} from "../../ViewRuleDatabase/Redux/ruleDatabaseSlice";

function FieldValue({ loading, children, fallback = "—" }) {
  if (loading) {
    return <span className="viewActiveRule-value--loading">Loading...</span>;
  }
  return <>{children ?? fallback}</>;
}

// Renders "Type — Value", but never prints a literal "null" if either
// piece is missing.
function formatPathwayEntry(pathwayEntry) {
  if (!pathwayEntry) return null;
  const { type, value } = pathwayEntry;
  if (type && value) return `${type} — ${value}`;
  return type || value || null;
}

export default function ViewActiveRule() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const codeAssistManualRuleId = useSelector(
    (state) => state.ruleDatabase.selectedEditRuleId
  );

  const data = useSelector((state) => state.ruleDatabase.ruleDraft);
  const loading = useSelector((state) => state.ruleDatabase.ruleDraftLoading);
  const error = useSelector((state) => state.ruleDatabase.ruleDraftError);

  const isInitialLoading = loading && !data;

  useEffect(() => {
    if (!codeAssistManualRuleId) {
      toast.error("No rule was selected to view.");
      return;
    }

    dispatch(
      viewCodeAssistRules({
        code_assist_manual_rule_id: codeAssistManualRuleId,
      })
    );

    return () => {
      dispatch(clearRuleDraft());
    };
  }, [dispatch, codeAssistManualRuleId]);

  const handleViewHistory = () => {
    if (!data) return;
    navigate(`/rule-version-history/${data.rule_id}`);
  };

  if (!codeAssistManualRuleId) {
    return (
      <div className="viewActiveRule-page">
        <p className="viewActiveRule-emptyState">No rule was selected to view.</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="viewActiveRule-page">
        <p className="viewActiveRule-errorState" role="alert">
          {error}
        </p>
      </div>
    );
  }

  const info = data?.rule_information || {};
  const config = data?.rule_configuration || {};
  const guidance = data?.guidance_decision_support || null;
  const approval = data?.approval_information || null;
  const publishing = data?.publishing_information || null;
  const triggerCodes = Array.isArray(config.trigger_codes) ? config.trigger_codes : [];
  const pathwayText = formatPathwayEntry(config.pathway_entry);

  // Guidance & Decision Support only applies to Draft rules — Active rules
  // have moved past that stage and show Approval/Publishing instead.
  const isDraft = data?.status === "Draft";
  const isActive = data?.status ==="Active"

  return (
    <div className="viewActiveRule-page">
      {/* Header */}
      <div className="viewActiveRule-header">
        <div className="viewActiveRule-header-title-row">
          <div className="viewActiveRule-header-left">
            <h1 className="viewActiveRule-title">
              <FieldValue loading={isInitialLoading}>{data?.description}</FieldValue>
            </h1>
            <span className="viewActiveRule-id-pill">
              <FieldValue loading={isInitialLoading}>{data?.rule_id}</FieldValue>
            </span>
            <span className="viewActiveRule-status-badge viewActiveRule-status-badge--active">
              <span className="viewActiveRule-status-dot" aria-hidden="true" />
              <FieldValue loading={isInitialLoading}>
                {(data?.status || "").toUpperCase()}
              </FieldValue>
            </span>
          </div>
          <button
            type="button"
            className="viewActiveRule-btn viewActiveRule-btn--history"
            onClick={handleViewHistory}
            disabled
          >
            <span className="viewActiveRule-btn-icon" aria-hidden="true">
              <img src={resetIcon} alt="version" />
            </span>
            View Version History
          </button>
        </div>
      </div>

      {/* Rule Information */}
      <section className="viewActiveRule-card">
        <div className="viewActiveRule-card-header">
          <span className="viewActiveRule-card-icon" aria-hidden="true">
            &#9432;
          </span>
          Rule Information
        </div>
        <div className="viewActiveRule-card-body">
          <div className="viewActiveRule-grid">
            <div className="viewActiveRule-field">
              <div className="viewActiveRule-label">Rule ID</div>
              <div className="viewActiveRule-value">
                <FieldValue loading={isInitialLoading}>{data?.rule_id}</FieldValue>
              </div>
            </div>
            <div className="viewActiveRule-field">
              <div className="viewActiveRule-label">Author</div>
              <div className="viewActiveRule-value">
                <FieldValue loading={isInitialLoading}>{info.author}</FieldValue>
              </div>
            </div>

            <div className="viewActiveRule-field">
              <div className="viewActiveRule-label">Creation Date</div>
              <div className="viewActiveRule-value">
                <FieldValue loading={isInitialLoading}>{info.created_on}</FieldValue>
              </div>
            </div>
            <div className="viewActiveRule-field">
              <div className="viewActiveRule-label">Clinical Domain</div>
              <div className="viewActiveRule-value">
                <span className="viewActiveRule-domainTag">
                  <FieldValue loading={isInitialLoading}>{info.clinical_domain}</FieldValue>
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rule Configuration */}
      <section className="viewActiveRule-card">
        <div className="viewActiveRule-card-header">
          <span className="viewActiveRule-card-icon" aria-hidden="true">
            &#9635;
          </span>
          Rule Configuration
        </div>
        <div className="viewActiveRule-card-body">
          <div className="viewActiveRule-field">
            <div className="viewActiveRule-label">Pathway Entry Point</div>
            <div className="viewActiveRule-value">
              <FieldValue loading={isInitialLoading}>{pathwayText}</FieldValue>
            </div>
          </div>

          <div className="viewActiveRule-field viewActiveRule-field--spaced">
            <div className="viewActiveRule-label">Trigger Codes</div>
            <div className="viewActiveRule-tags">
              {isInitialLoading ? (
                <FieldValue loading />
              ) : triggerCodes.length === 0 ? (
                <span className="viewActiveRule-value">No trigger codes.</span>
              ) : (
                triggerCodes.map((item) => (
                  <span className="viewActiveRule-tag" key={item.code}>
                    <span className="viewActiveRule-tag-code">{item.code}</span>
                    <span className="viewActiveRule-tag-desc">
                      {item.description}
                    </span>
                  </span>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Guidance & Decision Support — Draft rules only */}
      {isDraft && guidance && (
        <section className="viewActiveRule-card">
          <div className="viewActiveRule-card-header">
            Guidance &amp; Decision Support
          </div>
          <div className="viewActiveRule-card-body viewActiveRule-card-body--rows">
            <div className="viewActiveRule-row">
              <div className="viewActiveRule-rowLabel">Configured Guidance</div>
              <div className="viewActiveRule-rowValue">
                {guidance.configured_guidance || "—"}
              </div>
            </div>
            <div className="viewActiveRule-row viewActiveRule-row--last">
              <div className="viewActiveRule-rowLabel">Recommended Action</div>
              <div className="viewActiveRule-rowValue">
                {guidance.recommended_action || "—"}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Approval Information — only Approved By / Approved On */}
      {isActive && approval && (
        <section className="viewActiveRule-card">
          <div className="viewActiveRule-card-header">
            <span className="viewActiveRule-card-icon" aria-hidden="true">
              &#10003;
            </span>
            Approval Information
          </div>
          <div className="viewActiveRule-card-body viewActiveRule-card-body--rows">
            <div className="viewActiveRule-row">
              <div className="viewActiveRule-rowLabel">Approved By</div>
              <div className="viewActiveRule-rowValue">
                 <FieldValue loading={isInitialLoading}>{approval?.approved_by}</FieldValue>
                </div>
            </div>
            <div className="viewActiveRule-row viewActiveRule-row--last">
              <div className="viewActiveRule-rowLabel">Approved On</div>
              <div className="viewActiveRule-rowValue">
                <FieldValue loading={isInitialLoading}>{approval?.approved_on}</FieldValue>
                </div>
            </div>
          </div>
        </section>
      )}

      {/* Publishing Information — only present once a rule has been published */}
      {isActive && publishing && (
        <section className="viewActiveRule-card viewActiveRule-card--publishing">
          <div className="viewActiveRule-card-header">
            <span className="viewActiveRule-card-icon" aria-hidden="true">
              &#9998;
            </span>
            Publishing Information
          </div>
          <div className="viewActiveRule-card-body viewActiveRule-card-body--rows">
            <div className="viewActiveRule-row">
              <div className="viewActiveRule-rowLabel">Publish Status</div>
              <div className="viewActiveRule-rowValue viewActiveRule-rowValue--link">
                <FieldValue loading={isInitialLoading}>{publishing?.status}</FieldValue>
              </div>
            </div>
            <div className="viewActiveRule-row">
              <div className="viewActiveRule-rowLabel">Publishing Environment</div>
              <div className="viewActiveRule-rowValue">
                <FieldValue loading={isInitialLoading}>
                  {Array.isArray(publishing?.environment)
                    ? publishing.environment.join(", ")
                    : publishing?.environment}
                </FieldValue>
              </div>
            </div>
            <div className="viewActiveRule-row">
              <div className="viewActiveRule-rowLabel">Published By</div>
              <div className="viewActiveRule-rowValue">
                <FieldValue loading={isInitialLoading}>{publishing?.published_by}</FieldValue>
                </div>
            </div>
            <div className="viewActiveRule-row viewActiveRule-row--last">
              <div className="viewActiveRule-rowLabel">Published On</div>
              <div className="viewActiveRule-rowValue">
                <FieldValue loading={isInitialLoading}>{publishing?.published_on}</FieldValue>
                </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}